<template>
  <v-row justify="center">
    <v-dialog v-model="dialog" scrollable :max-width="$vuetify.breakpoint.smAndDown ? '96vw' : '680px'">
      <v-card>
        <v-card-title class="vms-form-title">
          {{ visitor?.id ? 'Review visit' : 'Invite visitor' }}
        </v-card-title>
        <v-card-subtitle class="vms-form-subtitle" v-if="!visitor?.id">
          Branch location and workflow fill in automatically. You can refine the purpose note before saving.
        </v-card-subtitle>

        <v-divider></v-divider>

        <v-card-text class="vms-form-card">
          <v-form class="vms-form" ref="form" v-model="valid" lazy-validation>
            <!-- Guest -->
            <div class="vms-form-section">
              <span class="vms-form-section__label">Guest</span>
              <v-row>
                <v-col cols="12" sm="6">
                  <v-text-field
                    dense
                    hide-details="auto"
                    v-model="visitor.guest_name"
                    label="Full name"
                    :error-messages="apiErrors.guest_name"
                    outlined
                    :rules="rules.required"
                    :readonly="visitor?.id ? true : false"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="vms-form-phone">
                    <vue-phone-number-input
                      v-model="phoneNumber"
                      @update="updatePhoneNumber"
                      :default-country-code="national_number"
                      size="sm"
                      :readonly="visitor?.id ? true : false"
                    ></vue-phone-number-input>
                    <span class="text-caption red--text" v-if="apiErrors.phone">{{ apiErrors.phone[0] }}</span>
                    <span class="text-caption red--text" v-else-if="phoneValidationError">{{ phoneValidationError }}</span>
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    dense
                    hide-details="auto"
                    v-model="visitor.id_number"
                    label="National ID"
                    :error-messages="apiErrors.id_number"
                    outlined
                    :rules="rules.id_number"
                    maxlength="20"
                    prepend-inner-icon="mdi-card-account-details-outline"
                    :readonly="visitor?.id ? true : false"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select
                    dense
                    hide-details="auto"
                    v-model="visitor.category"
                    :items="visitCategories"
                    label="Visit category"
                    :error-messages="apiErrors.category"
                    outlined
                  ></v-select>
                </v-col>
              </v-row>
            </div>

            <!-- Location -->
            <div class="vms-form-section" v-if="availableOrganizations.length || availableFacilities.length || approvalWorkflows.length">
              <span class="vms-form-section__label">Location &amp; workflow</span>
              <v-row>
                <v-col cols="12" sm="6" v-if="availableOrganizations.length > 0">
                  <v-select
                    dense
                    hide-details="auto"
                    v-model="selectedOrganizationId"
                    :items="availableOrganizations"
                    label="Organization / branch"
                    :error-messages="apiErrors.organization_id"
                    item-text="legal_name"
                    item-value="id"
                    outlined
                    :rules="rules.select"
                    @change="selectOrganization"
                    :readonly="!canChangeOrganization || !!visitor?.id"
                  ></v-select>
                </v-col>
                <v-col
                  cols="12"
                  :sm="availableOrganizations.length > 0 ? 6 : 12"
                  v-if="availableFacilities.length > 0"
                >
                  <v-select
                    dense
                    hide-details="auto"
                    v-model="visitor.facility_id"
                    :items="availableFacilities"
                    label="Facility / location"
                    :error-messages="apiErrors.facility_id"
                    item-text="facility"
                    item-value="id"
                    outlined
                    :rules="rules.select"
                    :auto-select-first="true"
                    :readonly="facilityLocked || !!visitor?.id"
                  ></v-select>
                </v-col>
                <v-col cols="12" v-if="approvalWorkflows.length > 0">
                  <v-select
                    :key="`workflow-select-${workflowSelectKey}`"
                    dense
                    hide-details="auto"
                    v-model="visitor.workflow_id"
                    :items="workflowSelectItems"
                    label="Approval workflow"
                    :error-messages="apiErrors.workflow_id"
                    item-text="title"
                    item-value="id"
                    outlined
                    :rules="rules.select"
                    readonly
                  ></v-select>
                </v-col>
              </v-row>
            </div>

            <!-- Schedule -->
            <div class="vms-form-section">
              <span class="vms-form-section__label">Schedule</span>
              <v-row>
                <v-col cols="12" sm="4">
                  <v-menu :close-on-content-click="closeOnContentClick" max-width="290px" min-width="290px">
                    <template v-slot:activator="{ on: menu, attrs }">
                      <v-text-field
                        dense
                        hide-details="auto"
                        v-model="dateToday"
                        label="Date"
                        prepend-inner-icon="mdi-calendar"
                        v-bind="attrs"
                        v-on="{ ...menu }"
                        :error-messages="apiErrors.event_date"
                        outlined
                        :readonly="visitor?.id ? true : false"
                      ></v-text-field>
                    </template>
                    <v-date-picker v-model="dateToday" color="primary" header-color="primary"></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="6" sm="4">
                  <v-menu :close-on-content-click="closeOnContentClick" max-width="290px" min-width="290px">
                    <template v-slot:activator="{ on: menu, attrs }">
                      <v-text-field
                        dense
                        hide-details="auto"
                        v-model="startTime"
                        label="From"
                        prepend-inner-icon="mdi-clock-outline"
                        v-bind="attrs"
                        v-on="{ ...menu }"
                        :error-messages="apiErrors.from_time"
                        outlined
                        :readonly="visitor?.id ? true : false"
                      ></v-text-field>
                    </template>
                    <v-time-picker v-model="startTime" scrollable format="24hr"></v-time-picker>
                  </v-menu>
                </v-col>
                <v-col cols="6" sm="4">
                  <v-menu :close-on-content-click="closeOnContentClick" max-width="290px" min-width="290px">
                    <template v-slot:activator="{ on: menu, attrs }">
                      <v-text-field
                        dense
                        hide-details="auto"
                        v-model="endTime"
                        label="To"
                        prepend-inner-icon="mdi-clock-outline"
                        v-bind="attrs"
                        v-on="{ ...menu }"
                        :error-messages="apiErrors.end_time"
                        outlined
                        :readonly="visitor?.id ? true : false"
                      ></v-text-field>
                    </template>
                    <v-time-picker v-model="endTime" scrollable format="24hr"></v-time-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" v-if="organizationUsers.length > 0">
                  <v-autocomplete
                    dense
                    hide-details="auto"
                    v-model="visitor.host_id"
                    :search-input.sync="search"
                    :items="organizationUsers"
                    item-value="id"
                    item-text="attributes.name"
                    label="Host"
                    prepend-inner-icon="mdi-account-tie-outline"
                    :error-messages="apiErrors.host_id"
                    outlined
                    autocomplete="off"
                    :loading="loading"
                    hide-no-data
                    hide-selected
                    :no-filter="true"
                    :readonly="visitor?.id ? true : false"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </div>

            <!-- AI assist (invite) -->
            <div class="vms-form-section" v-if="!visitor?.id">
              <span class="vms-form-section__label">Smart assist</span>
              <div class="d-flex flex-wrap" style="gap: 8px">
                <v-btn
                  small
                  outlined
                  color="primary"
                  class="text-capitalize"
                  :loading="aiLoading"
                  @click="improveInviteMessage"
                >
                  <v-icon left small>mdi-auto-fix</v-icon>
                  Improve message
                </v-btn>
                <v-btn
                  small
                  outlined
                  color="primary"
                  class="text-capitalize"
                  :loading="categoryLoading"
                  @click="suggestCategory"
                >
                  <v-icon left small>mdi-tag-search-outline</v-icon>
                  Suggest category
                </v-btn>
                <v-btn
                  small
                  outlined
                  color="primary"
                  class="text-capitalize"
                  :loading="duplicateLoading"
                  @click="runDuplicateCheck"
                >
                  <v-icon left small>mdi-account-search-outline</v-icon>
                  Check duplicates
                </v-btn>
              </div>
              <v-alert
                v-if="duplicateSummary"
                dense
                text
                :type="duplicateHasWatchlist ? 'warning' : 'info'"
                class="mt-3 mb-0"
                border="left"
              >
                {{ duplicateSummary }}
                <ul v-if="duplicateMatches.length" class="mb-0 mt-1 pl-4">
                  <li v-for="m in duplicateMatches.slice(0, 3)" :key="`dup-${m.user_id}`">
                    {{ m.name }}
                    <span v-if="m.id_number">(ID {{ m.id_number }})</span>
                    · {{ m.match_type }}
                    <span v-if="m.blacklisted" class="error--text font-weight-medium"> · watchlist</span>
                  </li>
                </ul>
              </v-alert>
            </div>

            <!-- AI assist (approver) -->
            <div class="vms-form-section" v-if="approvalStatus(visitor)">
              <span class="vms-form-section__label">Approver assist</span>
              <v-alert
                v-if="approvalBriefing"
                dense
                text
                type="info"
                class="mb-3"
                border="left"
              >
                {{ approvalBriefing }}
              </v-alert>
              <div class="d-flex flex-wrap mb-2" style="gap: 8px">
                <v-btn
                  small
                  outlined
                  color="primary"
                  class="text-capitalize"
                  :loading="briefingLoading"
                  @click="loadApprovalBriefing"
                >
                  <v-icon left small>mdi-text-box-outline</v-icon>
                  Refresh briefing
                </v-btn>
                <v-btn
                  small
                  outlined
                  color="warning"
                  class="text-capitalize"
                  :loading="draftLoading"
                  @click="loadDecisionDraft('reject')"
                >
                  Draft rejection
                </v-btn>
                <v-btn
                  small
                  outlined
                  color="info"
                  class="text-capitalize"
                  :loading="draftLoading"
                  @click="loadDecisionDraft('ask_info')"
                >
                  Draft ask-for-info
                </v-btn>
              </div>
              <v-textarea
                v-if="decisionDraft"
                dense
                hide-details="auto"
                v-model="decisionDraft"
                label="Suggested message (copy into notes / email)"
                outlined
                rows="3"
                auto-grow
              ></v-textarea>
            </div>

            <!-- Purpose / details -->
            <div class="vms-form-section">
              <span class="vms-form-section__label">Visit details</span>
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    dense
                    hide-details="auto"
                    v-model="visitor.title"
                    label="Visit title"
                    :error-messages="apiErrors.title"
                    outlined
                    :readonly="visitor?.id ? true : false"
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    dense
                    hide-details="auto"
                    v-model="purposeNotes"
                    label="Purpose / notes"
                    outlined
                    rows="3"
                    auto-grow
                    :readonly="visitor?.id ? true : false"
                  ></v-textarea>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    dense
                    hide-details="auto"
                    v-model="visitor.assets"
                    label="Assets (optional)"
                    :error-messages="apiErrors.assets"
                    placeholder="e.g. laptop, sample bag, tools"
                    outlined
                    rows="2"
                    auto-grow
                    :readonly="visitor?.id ? true : false"
                  ></v-textarea>
                </v-col>
              </v-row>
            </div>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="vms-form-actions">
          <v-btn
            text
            color="grey darken-1"
            @click="closeModal"
            class="text-capitalize"
          >
            Cancel
          </v-btn>

          <v-spacer></v-spacer>

          <v-btn
            v-if="approvalStatus(visitor)"
            color="warning darken-1"
            outlined
            class="text-capitalize mr-2"
            :loading="loading"
            @click="rejectVisit"
          >
            Reject
          </v-btn>
          <v-btn
            v-if="approvalStatus(visitor)"
            color="success"
            class="text-capitalize white--text"
            :loading="loading"
            @click="approveVisit"
          >
            Approve
          </v-btn>
          <v-btn
            v-if="!visitor?.id"
            color="primary"
            class="text-capitalize white--text"
            :loading="submitting"
            @click="saveVisit"
          >
            Send invite
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import Visitor from '../models/visitor';
import EventsService from '../services/events.service';
import OrganizationService from '../services/organization.service';
import VmsService from '../services/vms.service';

export default {
  name: 'ScheduleVisitModal',
  props: {
    value: {
      type: Boolean,
      required: true
    },
    organizationFacilities: {
      type: Array,
      required: true
    },
    approvalWorkflows: {
      type: Array,
      required: false,
      default: () => [],
    },
    organization: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    selectedDate: {
      type: String,
      required: false
    },
    host: {
      type: Object,
      required: false
    },
    selectedVisit: {
      type: Object,
      required: false
    },
  },
  data() {
    return {
      options: {
        page: 1,
        itemsPerPage: 10,
        filter: null
      },
      search: null,
      organizationUsers: [],
      selectedOrganizationId: null,
      visitor: new Visitor(),
      purposeNotes: '',
      national_number: "KE",
      phoneNumber: null,
      phoneValidationError: null,
      dateToday: new Date().toISOString().substr(0, 10),
      startTime: null,
      endTime: null,
      visitCategories: [
        "Official",
        "Personal",
        "Suppliers",
        "Commercial Bank Representatives",
        "Microfinance & Forex Bureau Officials",
        "Digital Financial Services & FinTech Providers",
        "On-Site Inspection Teams / Auditors",
        "M-Pesa / Mobile Money Operators",
        "National Treasury Officials",
        "Kenya Revenue Authority (KRA) Representatives",
        "State Attorneys & Law Enforcement",
        "Parastatal & State Corporation Executives",
        "Treasury Bills & Bonds Investors",
        "Primary Debt Dealers / Market Makers",
        "Foreign Exchange Dealers",
        "International Monetary Fund (IMF) & World Bank Delegates",
        "Regional Central Bank Delegations",
        "IT & ERP Infrastructure Vendors",
        "Currency & Minting Logistics Providers",
        "General Facility Procurement Vendors",
        "Job Candidates & Interviewees",
        "Academic Researchers & Interns",
        "VVIPs & Diplomatic Missions"
      ],
      apiErrors: {},
      valid: false,
      submitting: false,
      loading: false,
      aiLoading: false,
      categoryLoading: false,
      duplicateLoading: false,
      briefingLoading: false,
      draftLoading: false,
      approvalBriefing: '',
      decisionDraft: '',
      duplicateSummary: '',
      duplicateMatches: [],
      duplicateHasWatchlist: false,
      closeOnContentClick: false,
      rules: {
        select: [(v) => !!v || "An item should be selected"],
        select2: [(v) => v.length > 0 || "Atleast one item should be selected"],
        required: [
          (v) => !!v || "Field is required",
          (v) => (v && v.length >= 3) || "Min 3 characters",
        ],
        file: [
          (v) => !!v || "File is required",
          (v) => (v && v.length > 0) || "File is required",
        ],
        id_number: [
          (v) => !!v || "ID Number is required",
          (v) => (v && v.length >= 6) || "Min 6 characters",
          (v) => (v && v.length <= 20) || "ID Number must be at most 20 characters"
        ],
      },
    }
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    },
    user() {
      return JSON.parse(localStorage.getItem("user"));
    },
    userOrganizations() {
      return this.user?.organisations ?? [];
    },
    isAdminUser() {
      const v = this.user?.is_admin;
      return v === true || v === 1 || v === '1';
    },
    canChangeOrganization() {
      return this.isAdminUser && this.userOrganizations.length > 1;
    },
    availableOrganizations() {
      const orgs = (this.userOrganizations || []).map((o) => ({
        ...o,
        id: Number(o.id),
        legal_name:
          o.legal_name ||
          o.attributes?.legal_name ||
          o.name ||
          o.text ||
          `Organization #${o.id}`,
      }));

      if (this.isAdminUser) {
        return orgs;
      }

      const currentId = Number(this.organization?.id || this.selectedOrganizationId || 0);
      const match = orgs.filter((o) => Number(o.id) === currentId);
      if (match.length) {
        return match;
      }

      // Host with one membership (or prop not yet synced): show their org(s).
      return orgs.length ? orgs.slice(0, 1) : [];
    },
    availableFacilities() {
      return this.organizationFacilities || [];
    },
    facilityLocked() {
      return !this.isAdminUser && this.availableFacilities.length <= 1;
    },
    workflowSelectItems() {
      return (this.approvalWorkflows || []).map((wf) => ({
        ...wf,
        id: Number(wf.id),
        title: wf.title,
      }));
    },
    workflowSelectKey() {
      const ids = this.workflowSelectItems.map((w) => w.id).join('-');
      return `${ids}-${this.visitor?.workflow_id || 'none'}`;
    },
    isHeadOfficeOrg() {
      const org = this.organization || {};
      if (Number(org.isParentOrganization) === 1) return true;
      if (
        org.id_parent !== null
        && org.id_parent !== undefined
        && Number(org.id_parent) !== 0
      ) {
        return false;
      }
      if (this.workflowSelectItems.length === 1) {
        return Boolean(this.workflowSelectItems[0].is_parent_workflow);
      }
      return true;
    },
  },
  watch: {
    dialog: {
      handler(value) {
        if (value) {
          this.getUsers();
          if (this.visitor) {
            this.visitor.category = "Official";
          }
          this.ensureOrganizationSelected(true);
          this.autofillFacility();
          this.calculateTime();
          this.$nextTick(() => this.syncWorkflowSelection(true));
        }
      },
      deep: true
    },
    organizationFacilities: {
      handler() {
        this.autofillFacility();
      },
      deep: true
    },
    search: {
      handler(value) {
        this.getUsers(value);
      },
      deep: true
    },
    selectedDate: {
      handler(value) {
        this.dateToday = value;
      },
      deep: true
    },
    approvalWorkflows: {
      handler() {
        if (this.dialog) {
          this.$nextTick(() => this.syncWorkflowSelection(true));
        }
      },
      immediate: true,
      deep: true,
    },
    organization: {
      handler() {
        if (this.dialog && !this.visitor?.id) {
          this.ensureOrganizationSelected(false);
          this.$nextTick(() => this.syncWorkflowSelection(true));
        }
      },
      deep: true,
    },
    selectedVisit: {
      handler(newVal) {
        const isEmpty =
          !newVal
          || (typeof newVal === 'object'
            && !newVal.id
            && !newVal.vst_id
            && !newVal.guest_name);

        if (!isEmpty) {
          this.visitor = { ...new Visitor(), ...newVal };
          this.phoneNumber = newVal.guest_phone;
          this.dateToday = newVal.event_date;
          this.startTime = newVal.from_time;
          this.endTime = newVal.end_time;
          this.purposeNotes = newVal.notes || newVal.purpose_notes || '';
          if (newVal.assets != null) {
            this.visitor.assets = newVal.assets;
          }
          this.getUsers();
          this.$nextTick(() => {
            if (this.approvalStatus(this.visitor)) {
              this.loadApprovalBriefing();
            }
          });
        } else {
          this.visitor = new Visitor();
          this.phoneNumber = null;
          this.purposeNotes = '';
          this.approvalBriefing = '';
          this.decisionDraft = '';
          this.duplicateSummary = '';
          this.duplicateMatches = [];
          this.duplicateHasWatchlist = false;
          this.dateToday = new Date().toISOString().substr(0, 10);
          this.autofillFacility();
          this.calculateTime();
          this.$nextTick(() => this.syncWorkflowSelection(true));
        }
      },
      immediate: true
    },
  },
  created() {
    this.visitor = new Visitor();
    this.calculateTime();
  },
  methods: {
    syncWorkflowSelection(force = false) {
      if (!this.visitor || this.visitor.id) return;

      const items = this.workflowSelectItems;
      if (!items.length) {
        this.$set(this.visitor, 'workflow_id', null);
        return;
      }

      const preferParent = this.isHeadOfficeOrg;
      const preferred =
        items.find((wf) => Boolean(wf.is_parent_workflow) === preferParent)
        || items[0];

      const preferredId = preferred ? Number(preferred.id) : null;
      const currentId = this.visitor.workflow_id != null
        ? Number(this.visitor.workflow_id)
        : null;
      const currentStillValid = items.some((wf) => Number(wf.id) === currentId);

      if (force || !currentStillValid || currentId !== preferredId) {
        this.$set(this.visitor, 'workflow_id', preferredId);
      }
    },
    autofillFacility() {
      if (this.visitor?.id) return;
      if (!this.visitor.facility_id && this.availableFacilities.length > 0) {
        this.$set(this.visitor, 'facility_id', this.availableFacilities[0].id);
      }
    },
    ensureOrganizationSelected(emitIfNeeded = false) {
      const orgs = this.availableOrganizations;
      if (!orgs.length) {
        this.selectedOrganizationId = null;
        return;
      }

      const propId = this.organization?.id != null ? Number(this.organization.id) : null;
      const currentId = this.selectedOrganizationId != null ? Number(this.selectedOrganizationId) : null;
      const validCurrent = orgs.some((o) => Number(o.id) === currentId);
      const validProp = orgs.some((o) => Number(o.id) === propId);

      let nextId = validCurrent ? currentId : (validProp ? propId : Number(orgs[0].id));
      this.selectedOrganizationId = nextId;

      const propMissingOrDifferent = Number(this.organization?.id) !== Number(nextId);
      if (emitIfNeeded && propMissingOrDifferent && !this.visitor?.id) {
        this.emitOrganizationSelection(nextId);
      }
    },
    resolveOrganizationById(orgId) {
      const matched = this.availableOrganizations.find(
        (o) => Number(o.id) === Number(orgId)
      );

      if (matched) {
        return {
          ...matched,
          id: matched.id,
          legal_name: matched.legal_name,
        };
      }

      return {
        ...(this.organization || {}),
        id: orgId,
        legal_name:
          this.organization?.legal_name ||
          this.organization?.attributes?.legal_name ||
          this.organization?.name,
      };
    },
    emitOrganizationSelection(orgId) {
      this.$emit("select_organization", {
        selectedTabId: 2,
        selectedOrganization: this.resolveOrganizationById(orgId),
        keepInviteOpen: true,
      });
    },
    selectOrganization() {
      this.$set(this.visitor, 'facility_id', null);
      this.$set(this.visitor, 'workflow_id', null);
      this.getUsers();
      this.emitOrganizationSelection(this.selectedOrganizationId);
    },
    calculateTime() {
      let time = new Date(Date.now());
      let hour = time.getHours();
      let minutes = time.getMinutes();

      if (minutes > 30) {
        hour += 2;
      } else {
        hour += 1;
      }

      this.startTime = hour + ":00";
      this.endTime = (hour + 1) + ":00";
    },
    closeModal() {
      this.phoneNumber = null;
      this.purposeNotes = '';
      this.approvalBriefing = '';
      this.decisionDraft = '';
      this.duplicateSummary = '';
      this.duplicateMatches = [];
      this.duplicateHasWatchlist = false;
      this.visitor = new Visitor();
      this.dialog = false;
    },
    async improveInviteMessage() {
      const beforeTitle = (this.visitor.title || '').trim();
      const beforePurpose = (this.purposeNotes || '').trim();
      if (!beforeTitle && !beforePurpose && !this.visitor.guest_name) {
        this.$store.dispatch(
          "alert/error",
          "Enter a visit title or purpose first, then click Improve message."
        );
        return;
      }

      this.aiLoading = true;
      try {
        const response = await VmsService.inviteAssist({
          organization_id: this.selectedOrganizationId || this.organization?.id,
          guest_name: this.visitor.guest_name,
          category: this.visitor.category,
          title: this.visitor.title,
          assets: this.purposeNotes,
          facility_id: this.visitor.facility_id,
          event_date: this.dateToday,
          from_time: this.startTime,
        });
        const data = response?.data?.data ?? response?.data ?? {};
        if (data.title) this.visitor.title = data.title;
        if (data.assets || data.purpose) {
          this.purposeNotes = data.assets || data.purpose;
        }

        const afterTitle = (this.visitor.title || '').trim();
        const afterPurpose = (this.purposeNotes || '').trim();
        const changed = afterTitle !== beforeTitle || afterPurpose !== beforePurpose;

        if (data.source === 'llm') {
          this.$store.dispatch("alert/success", "Invite message improved with AI");
        } else if (data.source === 'llm_auth_error' || data.source === 'llm_parse_error') {
          this.$store.dispatch(
            "alert/error",
            data.hint || "AI provider rejected the request. Check your AI / LLM key, base URL, and model in System Configurations."
          );
        } else if (changed) {
          this.$store.dispatch(
            "alert/success",
            "Message polished (spelling/grammar). Add an AI / LLM key in System Configurations for fuller rewrites."
          );
        } else {
          this.$store.dispatch(
            "alert/success",
            "No spelling fixes needed. Add an AI / LLM key in System Configurations for richer rewrites."
          );
        }
      } catch (error) {
        this.$store.dispatch("alert/error", error.response?.data?.message || 'Could not improve invite message');
      } finally {
        this.aiLoading = false;
      }
    },
    async suggestCategory() {
      const purpose = (this.purposeNotes || this.visitor.title || '').trim();
      if (!purpose) {
        this.$store.dispatch('alert/error', 'Enter a purpose or title first, then suggest a category.');
        return;
      }
      this.categoryLoading = true;
      try {
        const response = await VmsService.suggestCategory({
          organization_id: this.selectedOrganizationId || this.organization?.id,
          purpose,
          title: this.visitor.title,
          categories: this.visitCategories,
        });
        const data = response?.data?.data ?? response?.data ?? {};
        if (data.category) {
          this.visitor.category = data.category;
        }
        if (data.title_hint && !(this.visitor.title || '').trim()) {
          this.visitor.title = data.title_hint;
        }
        this.$store.dispatch(
          'alert/success',
          data.source === 'llm' ? 'Category suggested with AI' : 'Category suggested'
        );
      } catch (e) {
        this.$store.dispatch('alert/error', e.response?.data?.message || 'Could not suggest category');
      } finally {
        this.categoryLoading = false;
      }
    },
    async runDuplicateCheck() {
      if (!(this.visitor.guest_name || '').trim() && !(this.visitor.id_number || '').trim()) {
        this.$store.dispatch('alert/error', 'Enter a guest name or national ID to check duplicates.');
        return;
      }
      this.duplicateLoading = true;
      try {
        const response = await VmsService.duplicateCheck({
          organization_id: this.selectedOrganizationId || this.organization?.id,
          guest_name: this.visitor.guest_name,
          id_number: this.visitor.id_number,
          facility_id: this.visitor.facility_id,
        });
        const data = response?.data?.data ?? response?.data ?? {};
        this.duplicateMatches = data.matches || [];
        this.duplicateSummary = data.summary || 'No matches.';
        this.duplicateHasWatchlist = this.duplicateMatches.some((m) => m.blacklisted);
      } catch (e) {
        this.$store.dispatch('alert/error', e.response?.data?.message || 'Duplicate check failed');
      } finally {
        this.duplicateLoading = false;
      }
    },
    async loadApprovalBriefing() {
      const visitId = this.visitor?.id || this.visitor?.vst_id;
      if (!visitId) return;
      this.briefingLoading = true;
      try {
        const response = await VmsService.approvalBriefing(visitId);
        const data = response?.data?.data ?? response?.data ?? {};
        this.approvalBriefing = data.briefing || '';
      } catch (e) {
        // Non-blocking — approval still works without briefing.
        this.approvalBriefing = '';
      } finally {
        this.briefingLoading = false;
      }
    },
    async loadDecisionDraft(intent = 'reject') {
      const visitId = this.visitor?.id || this.visitor?.vst_id;
      if (!visitId) return;
      this.draftLoading = true;
      try {
        const response = await VmsService.decisionDraft(visitId, { intent });
        const data = response?.data?.data ?? response?.data ?? {};
        this.decisionDraft = data.draft || '';
      } catch (e) {
        this.$store.dispatch('alert/error', e.response?.data?.message || 'Could not draft message');
      } finally {
        this.draftLoading = false;
      }
    },
    saveVisit() {
      this.valid = this.$refs.form.validate();
      if (this.valid) {
        this.validatePhoneNumber();
        this.autofillFacility();
        this.syncWorkflowSelection(true);

        const purpose = (this.purposeNotes || '').trim();
        const assets = (this.visitor.assets || '').trim();

        const payload = {
          ...this.visitor,
          from_time: this.startTime,
          end_time: this.endTime,
          event_date: this.dateToday,
          // Keep purpose/notes and assets separate — do not merge into one field.
          assets: assets || null,
          notes: purpose || null,
        };

        this.submitting = true;

        EventsService.store(payload).then(
          (response) => {
            if (response.status === 200) {
              this.$store.dispatch("alert/success", "Visitor invited — approvers have been notified");
              this.loading = false;
              this.submitting = false;
              this.$emit('get_upcoming_visits');
            } else {
              this.submitting = false;
              this.$store.dispatch("alert/error", (this.user.name ?? "Visitor") + " not invited" + response.message);
            }

            this.closeModal();
          },
          (error) => {
            this.submitting = false;

            if (error.response.status === 422) {
              this.apiErrors = error.response.data.errors
              console.log(error.response.data.errors)
              this.$store.dispatch("alert/error", error.response.data.message);
              this.loading = false;
            }

            this.$store.dispatch("alert/error", error.response.data.message);
          }
        );
      } else {
        this.$store.dispatch("alert/error", 'Form is Invalid!');
      }
    },
    index() {
      this.$emit('get_upcoming_visits');
    },
    updatePhoneNumber(data) {
      this.visitor.guest_phone = data.e164
    },
    validatePhoneNumber() {
      if (this.phoneNumber == null) {
        this.phoneErrorColor = "red";
        this.phoneValidationError = "The phone number is required";
      }
    },
    getUsers(search = null) {
      this.loading = true;
      const orgId = this.selectedOrganizationId || this.organization?.id;
      if (!orgId) {
        this.organizationUsers = [];
        this.loading = false;
        return;
      }

      if (!this.host?.id && !this.visitor?.id) {
        this.options.filter = search;
      } else if (this.visitor?.id) {
        this.options.filter = this.visitor?.host_name ?? null;
      }

      OrganizationService.organization_users(orgId, { options: this.options }).then(
        success => {
          this.organizationUsers = success?.data?.data?.data ?? this.organizationUsers;
          this.loading = false;

          if (this.host?.id ?? this.visitor?.host_id) {
            let index = -1;
            index = this.organizationUsers.findIndex(user => user.id == (this.host?.id ?? this.visitor?.host_id));
            if (index > -1) {
              this.visitor.host_id = this.organizationUsers[index].id;
            }
          }
        },
        error => {
          console.log(error)
          this.organizationUsers = [];
          this.loading = false;
        }
      )
    },
    approvalStatus(item) {
      if (!item || !item.id) {
        return false;
      }

      if (item.execute == 1 || item.active == 0) {
        return false;
      }

      if (item.current_user === false || item.current_user === 0) {
        return false;
      }

      return true;
    },

    approveVisit() {
      this.loading = true;
      this.valid = this.$refs.form.validate();
      this.visitor.sender_id = this.user.id;
      if (this.valid) {
        EventsService.approve(this.visitor).then(
          (response) => {
            if (response.status == 200) {
              this.$store.dispatch("alert/success", response.data.message || "Visit approved successfully");
              this.closeModal();
            } else {
              this.$store.dispatch("alert/error", response.data.message || "Approval failed");
            }
            this.index();
            this.loading = false;
          },
          (error) => {
            const status = error.response?.status;
            const message = error.response?.data?.message || error.message || "Approval failed";

            if (status == 401) {
              this.handleLogout();
            } else {
              this.$store.dispatch("alert/error", message);
            }

            // Refresh anyway — last-step approval may have committed before a side-effect error.
            this.index();
            this.loading = false;
          }
        );
      } else {
        this.loading = false;
      }
    },

    rejectVisit() {
      this.loading = true;
      this.valid = this.$refs.form.validate();
      this.visitor.sender_id = this.user.id;
      if (this.valid) {
        EventsService.reject(this.visitor).then(
          (response) => {
            if (response.status == 200) {
              this.$store.dispatch("alert/success", response.data.message || "Visit rejected successfully");
              this.closeModal();
            } else {
              this.$store.dispatch("alert/error", response.data.message || "Rejection failed");
            }
            this.index();
            this.loading = false;
          },
          (error) => {
            const status = error.response?.status;
            const message = error.response?.data?.message || error.message || "Rejection failed";

            if (status == 401) {
              this.handleLogout();
            } else {
              this.$store.dispatch("alert/error", message);
            }

            this.index();
            this.loading = false;
          }
        );
      } else {
        this.loading = false;
      }
    },
  },
}
</script>
